import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileCheck, ShoppingCart, ArrowRight, CheckCircle, Globe, 
  TrendingUp, Shield, Percent, Building2, Truck, Package,
  Rocket, Store, CreditCard, Users, BarChart3, Zap,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import QuickContactModal from '../components/QuickContactModal';

const IndiaUAEPrograms = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState('');

  const programs = [
    {
      id: 'cepa',
      icon: FileCheck,
      title: 'CEPA Program',
      subtitle: 'Comprehensive Economic Partnership Agreement',
      description: 'Unlock massive tariff savings with the India-UAE Free Trade Agreement. Get zero or reduced tariffs on 80%+ of goods traded between the two nations.',
      color: 'from-blue-600 to-indigo-700',
      bgLight: 'bg-blue-50',
      highlights: [
        { icon: Percent, label: 'Zero Tariffs', value: '80%+ Products' },
        { icon: TrendingUp, label: 'Trade Growth', value: '$100B+' },
        { icon: Globe, label: 'Sectors', value: '100+ Sub-sectors' },
      ],
      benefits: [
        'Zero tariffs on pharmaceuticals, gems & jewellery',
        'Reduced duties on engineering goods & textiles',
        'Preferential market access for IT services',
        'Protection from anti-dumping investigations',
        'Faster customs clearance procedures',
        'Access to UAE government procurement'
      ],
      eligibleSectors: ['Pharmaceuticals', 'Gems & Jewellery', 'Textiles', 'Engineering Goods', 'IT Services', 'Food Products'],
      cta: 'Calculate CEPA Savings',
      link: '/india-uae-trade'
    },
    {
      id: 'ecommerce',
      icon: ShoppingCart,
      title: 'E-commerce Softlanding',
      subtitle: 'Launch Your Online Business in UAE',
      description: 'Fast-track your e-commerce expansion into the UAE and GCC markets. Get your online store operational with all required licenses and marketplace integrations.',
      color: 'from-emerald-500 to-teal-600',
      bgLight: 'bg-emerald-50',
      highlights: [
        { icon: Rocket, label: 'Launch Time', value: '2-4 Weeks' },
        { icon: Store, label: 'Marketplaces', value: 'Amazon, Noon+' },
        { icon: Users, label: 'Market Access', value: '50M+ Consumers' },
      ],
      benefits: [
        'E-commerce trade license setup',
        'Amazon UAE & Noon seller registration',
        'Payment gateway integration (local & international)',
        'Logistics & fulfillment partnerships',
        'VAT registration & compliance',
        'Dedicated UAE business address'
      ],
      eligibleSectors: ['Retail', 'Fashion', 'Electronics', 'Health & Beauty', 'Home & Living', 'Food & Grocery'],
      cta: 'Start E-commerce Journey',
      link: null
    }
  ];

  const ecommerceSoftlandingDetails = {
    packages: [
      {
        name: 'Starter',
        price: 'AED 15,000',
        period: 'One-time setup',
        features: [
          'E-commerce Trade License',
          'UAE Business Address',
          '1 Marketplace Registration',
          'Basic Payment Gateway',
          'VAT Registration',
          'Email Support'
        ],
        highlighted: false
      },
      {
        name: 'Growth',
        price: 'AED 25,000',
        period: 'One-time setup',
        features: [
          'Everything in Starter',
          '3 Marketplace Registrations',
          'Advanced Payment Solutions',
          'Logistics Setup (3 Partners)',
          'Inventory Management System',
          'Dedicated Account Manager'
        ],
        highlighted: true
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        period: 'Tailored solution',
        features: [
          'Everything in Growth',
          'Unlimited Marketplaces',
          'Custom Integrations',
          'Warehouse Solutions',
          'Multi-currency Support',
          'Priority 24/7 Support'
        ],
        highlighted: false
      }
    ],
    marketplaces: [
      { name: 'Amazon UAE', logo: '🛒', users: '5M+' },
      { name: 'Noon', logo: '🌙', users: '20M+' },
      { name: 'Namshi', logo: '👗', users: '3M+' },
      { name: 'Carrefour', logo: '🛍️', users: '10M+' }
    ]
  };

  const openModal = (program) => {
    setSelectedProgram(program);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1929] via-[#1a1033] to-[#0A1929] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-orange-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-500 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Special Programs for Indian Businesses
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              India-UAE <span className="text-[#DC143C]">Programs</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Exclusive programs designed to help Indian businesses expand into UAE. 
              From trade benefits under CEPA to seamless e-commerce market entry.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => openModal('India-UAE Programs')}
                className="bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white px-8 py-6 text-lg hover:shadow-xl transition-all"
              >
                Get Started
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('programs').scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Programs
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section id="programs" className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Choose Your <span className="text-[#DC143C]">Program</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select the program that best suits your business expansion goals
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {programs.map((program) => (
              <div 
                key={program.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group"
              >
                {/* Program Header */}
                <div className={`bg-gradient-to-r ${program.color} p-6 text-white`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <program.icon size={48} className="mb-4" />
                      <h3 className="text-2xl font-bold mb-1">{program.title}</h3>
                      <p className="text-white/80 text-sm">{program.subtitle}</p>
                    </div>
                    <ArrowRight className="opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1" size={24} />
                  </div>
                </div>

                {/* Program Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-6">{program.description}</p>

                  {/* Highlights */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {program.highlights.map((highlight, idx) => (
                      <div key={idx} className={`${program.bgLight} rounded-xl p-3 text-center`}>
                        <highlight.icon size={20} className="mx-auto mb-1 text-gray-700" />
                        <p className="text-lg font-bold text-gray-800">{highlight.value}</p>
                        <p className="text-xs text-gray-600">{highlight.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Benefits */}
                  <h4 className="font-semibold text-gray-800 mb-3">Key Benefits:</h4>
                  <ul className="space-y-2 mb-6">
                    {program.benefits.slice(0, 4).map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {benefit}
                      </li>
                    ))}
                  </ul>

                  {/* Eligible Sectors */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {program.eligibleSectors.map((sector, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                      >
                        {sector}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="flex gap-3">
                    {program.link ? (
                      <Link to={program.link} className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${program.color} text-white hover:shadow-lg`}>
                          {program.cta}
                          <ChevronRight className="ml-2" size={18} />
                        </Button>
                      </Link>
                    ) : (
                      <Button 
                        onClick={() => openModal(program.title)}
                        className={`flex-1 bg-gradient-to-r ${program.color} text-white hover:shadow-lg`}
                      >
                        {program.cta}
                        <ChevronRight className="ml-2" size={18} />
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      onClick={() => document.getElementById(program.id).scrollIntoView({ behavior: 'smooth' })}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEPA Program Details */}
      <section id="cepa" className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <FileCheck className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800">CEPA Program</h2>
                <p className="text-gray-600">India-UAE Comprehensive Economic Partnership Agreement</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Left - Info */}
              <div>
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">What is CEPA?</h3>
                  <p className="text-gray-600 mb-4">
                    The Comprehensive Economic Partnership Agreement (CEPA) between India and UAE, 
                    effective from May 2022, is a landmark trade agreement that provides preferential 
                    market access, reduced tariffs, and enhanced investment opportunities.
                  </p>
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Bilateral Trade:</strong> Over $100 billion in FY 2024-25, making UAE India's 
                      2nd largest trading partner.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">How Firmzy Helps</h3>
                  <ul className="space-y-3">
                    {[
                      'CEPA eligibility assessment',
                      'HS code identification & tariff analysis',
                      'Rules of Origin compliance',
                      'Certificate of Origin assistance',
                      'Customs documentation support',
                      'UAE entity setup for CEPA benefits'
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle size={18} className="text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right - Savings */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Tariff Savings by Sector</h3>
                <div className="space-y-4">
                  {[
                    { sector: 'Pharmaceuticals', saving: '100%', status: 'Zero Duty' },
                    { sector: 'Gems & Jewellery', saving: '100%', status: 'Zero Duty' },
                    { sector: 'Textiles & Apparel', saving: '90%', status: 'Reduced' },
                    { sector: 'Engineering Goods', saving: '85%', status: 'Reduced' },
                    { sector: 'Chemicals', saving: '80%', status: 'Reduced' },
                    { sector: 'Food Products', saving: '75%', status: 'Reduced' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-800">{item.sector}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold text-blue-600">{item.saving}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          item.status === 'Zero Duty' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/india-uae-trade">
                  <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white">
                    View Complete CEPA Benefits
                    <ExternalLink className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E-commerce Softlanding Details */}
      <section id="ecommerce" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                <ShoppingCart className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800">E-commerce Softlanding</h2>
                <p className="text-gray-600">Launch Your Online Business in UAE & GCC</p>
              </div>
            </div>

            {/* What's Included */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Complete E-commerce Setup Package</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: Building2,
                    title: 'Business Setup',
                    items: ['E-commerce License', 'UAE Address', 'Bank Account', 'VAT Registration']
                  },
                  {
                    icon: Store,
                    title: 'Marketplace Integration',
                    items: ['Amazon UAE', 'Noon', 'Namshi', 'Carrefour Online']
                  },
                  {
                    icon: Truck,
                    title: 'Operations',
                    items: ['Payment Gateway', 'Logistics Partners', 'Fulfillment Setup', 'Customer Support']
                  }
                ].map((category, idx) => (
                  <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                    <category.icon className="text-emerald-500 mb-3" size={28} />
                    <h4 className="font-bold text-gray-800 mb-3">{category.title}</h4>
                    <ul className="space-y-2">
                      {category.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle size={14} className="text-emerald-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Supported Marketplaces */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Supported Marketplaces</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ecommerceSoftlandingDetails.marketplaces.map((marketplace, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-5 text-center hover:shadow-md transition-all">
                    <span className="text-4xl mb-2 block">{marketplace.logo}</span>
                    <h4 className="font-semibold text-gray-800">{marketplace.name}</h4>
                    <p className="text-sm text-gray-500">{marketplace.users} Users</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Packages */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Softlanding Packages</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {ecommerceSoftlandingDetails.packages.map((pkg, idx) => (
                  <div 
                    key={idx} 
                    className={`rounded-2xl p-6 ${
                      pkg.highlighted 
                        ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-xl scale-105' 
                        : 'bg-white shadow-lg'
                    }`}
                  >
                    {pkg.highlighted && (
                      <span className="bg-white text-emerald-600 text-xs font-bold px-3 py-1 rounded-full mb-4 inline-block">
                        MOST POPULAR
                      </span>
                    )}
                    <h4 className={`text-xl font-bold mb-2 ${pkg.highlighted ? 'text-white' : 'text-gray-800'}`}>
                      {pkg.name}
                    </h4>
                    <p className={`text-3xl font-bold mb-1 ${pkg.highlighted ? 'text-white' : 'text-emerald-600'}`}>
                      {pkg.price}
                    </p>
                    <p className={`text-sm mb-6 ${pkg.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                      {pkg.period}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className={`flex items-center gap-2 text-sm ${pkg.highlighted ? 'text-white' : 'text-gray-700'}`}>
                          <CheckCircle size={16} className={pkg.highlighted ? 'text-white' : 'text-emerald-500'} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => openModal(`E-commerce ${pkg.name}`)}
                      className={`w-full ${
                        pkg.highlighted 
                          ? 'bg-white text-emerald-600 hover:bg-gray-100' 
                          : 'bg-emerald-500 text-white hover:bg-emerald-600'
                      }`}
                    >
                      Get Started
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Firmzy */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Why Choose <span className="text-[#DC143C]">Firmzy</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: 'Trusted Partner', desc: '500+ businesses setup' },
              { icon: Zap, title: 'Fast Processing', desc: '2-4 weeks launch time' },
              { icon: Users, title: 'Expert Team', desc: 'Dedicated account managers' },
              { icon: BarChart3, title: 'Proven Results', desc: '$10M+ client savings' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-all">
                <item.icon className="mx-auto mb-3 text-[#DC143C]" size={32} />
                <h4 className="font-bold text-gray-800 mb-1">{item.title}</h4>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#6B1B4D] to-[#DC143C] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Expand to UAE?
          </h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Join hundreds of Indian businesses successfully operating in UAE through our programs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => openModal('CEPA Consultation')}
              className="bg-white text-[#6B1B4D] hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
            >
              <FileCheck className="mr-2" size={20} />
              CEPA Consultation
            </Button>
            <Button 
              onClick={() => openModal('E-commerce Softlanding')}
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold"
            >
              <ShoppingCart className="mr-2" size={20} />
              E-commerce Setup
            </Button>
          </div>
        </div>
      </section>

      <QuickContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        defaultService={selectedProgram || 'Business Consultancy'}
      />
    </div>
  );
};

export default IndiaUAEPrograms;
